import * as ko from "knockout";
import {storeObservable} from "../../util/sessionstore";
import {config, global} from "../../appContext";
import {AVAILABLE_NUM_SEARCH_RESULTS, LATEST_OBJECTS_SHOW_LIMIT, ObjectTypeId} from "../../constants";
import {nsSettings} from "./fn";
import {PermissionValidator} from "../../auth/PermissionValidator";
import Config from "../../config";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {isNaN} from "lodash";
import {getSyncFieldValue} from "../../util/misc";
import {getHash} from "../../constants/RouteDefinitions";
import {nsLanguage} from "../../util/nsLanguage";
import {DynamicProperties} from "../form/template/properties/DynamicProperties";
import discardAllSettings = nsSettings.discardAllSettings;
import getLanguageProperty = nsLanguage.getLanguageProperty;
import sidepanelHtml from './sidepanel.html?raw';

export let template: string = sidepanelHtml;

type TSectionId =
    | 'general'
    | 'columns'
    | 'admintools'
    | 'user'
    | 'group'
    | 'department'
    | 'apiuser'
    | 'dynamicelement'
    | 'preview';

interface ISectionState {
    open: KnockoutObservable<boolean>;
    /** Present for heavy sections — latched true on first open so content stays mounted. */
    mounted?: KnockoutObservable<boolean>;
}

export class viewModel {

    private readonly config: Config = config
    private readonly global = global;
    private readonly pv: PermissionValidator = global.permissionValidator
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly login = global.login;
    private readonly numNewTasksAndMessages = global.login.numNewTasksAndMessages;
    private readonly rightPanelToggle: KnockoutObservable<boolean> = storeObservable('layout.right.toggle');

    // General settings observables
    private readonly AVAILABLE_NUM_SEARCH_RESULTS = AVAILABLE_NUM_SEARCH_RESULTS;
    private readonly cssBlockFullWidth: KnockoutObservable<boolean> = storeObservable('css.block.fullwidth');
    private readonly numSearchResults: KnockoutObservable<number> = storeObservable('num.search.results');
    private readonly numRulesetDerivedValues: KnockoutObservable<number> = storeObservable('num.ruleset.derived.values');
    private readonly productionWarning: KnockoutObservable<boolean> = storeObservable('production.warning');
    private readonly showLimit: KnockoutObservable<number> = storeObservable('latest.objects.show.limit');
    private readonly latestObjectsAutomatic: KnockoutObservable<boolean> = storeObservable('latest.objects.automatic');
    private readonly showSuperAdminEmulating: KnockoutObservable<boolean> = storeObservable('show.super.adm.emulating');
    private readonly useConsole: KnockoutObservable<number> = storeObservable('should.use.console');
    private readonly prefixTranslation = storeObservable('prefix.translation');
    private readonly showTranslationKey = storeObservable('show.translation.key');
    private readonly showComponentName: KnockoutObservable<boolean> = storeObservable('show.component.name');
    private readonly useEadmExpressionBuilderInEhub: KnockoutObservable<boolean> = storeObservable('eadm.expressionbuilder.ehub');
    private readonly sourceTranslation = global.sourceTranslation;

    private readonly toggleProductionWarning = storeObservable('production.warning');
    private readonly toggleSuperAdmEmulating = storeObservable('show.super.adm.emulating');

    private readonly isEmulated = global.loginManager.isEmulated.bind(global.loginManager)

    private readonly toggleDemoMode = ko.computed({
        read: () => !this.toggleProductionWarning() && !this.toggleSuperAdmEmulating(),
        write: (bool: boolean) => {
            this.toggleProductionWarning(!bool);
            this.toggleSuperAdmEmulating(!bool);
        }
    });

    // Admin tools (dynamic element + preview)
    readonly dynamicProperties = new DynamicProperties();

    /**
     * Single registry for accordion open state (session-backed) and lazy-mount latches.
     * Admin children reuse the existing details.element / details.preview store keys.
     */
    private readonly sectionState: Record<TSectionId, ISectionState> = {
        general: {open: storeObservable('sidepanel.section.general')},
        columns: {open: storeObservable('sidepanel.section.columns')},
        admintools: {open: storeObservable('sidepanel.section.admintools')},
        user: {
            open: storeObservable('sidepanel.section.user'),
            mounted: ko.observable(false),
        },
        group: {
            open: storeObservable('sidepanel.section.group'),
            mounted: ko.observable(false),
        },
        department: {
            open: storeObservable('sidepanel.section.department'),
            mounted: ko.observable(false),
        },
        apiuser: {
            open: storeObservable('sidepanel.section.apiuser'),
            mounted: ko.observable(false),
        },
        dynamicelement: {
            open: storeObservable('details.element.toggle'),
            mounted: ko.observable(false),
        },
        preview: {
            open: storeObservable('details.preview.toggle'),
            mounted: ko.observable(false),
        },
    };

    ObjectTypeId = ObjectTypeId;

    constructor() {
        this.kosub.subscribe(this.showLimit, (val: any) => {
            let v = parseInt(val);
            if (isNaN(v)) {
                this.showLimit(LATEST_OBJECTS_SHOW_LIMIT);
            } else {
                this.showLimit(v);
            }
        });

        // Restore lazy mounts for sections that were open in a previous session.
        (Object.keys(this.sectionState) as TSectionId[]).forEach((id) => {
            const state = this.sectionState[id];
            if (state.mounted && state.open()) {
                state.mounted(true);
            }
        });
    }

    private onSectionToggle(sectionId: TSectionId) {
        const state = this.sectionState[sectionId];
        const willOpen = !state.open();
        if (willOpen) {
            state.mounted?.(true);
        }
        state.open(willOpen);
    }

    /** Native <details> toggle for admin children — keeps open state in session store. */
    private onAdminToggle(sectionId: 'dynamicelement' | 'preview', _vm: viewModel, e: JQuery.TriggeredEvent) {
        const isOpen = !!(e.currentTarget as HTMLDetailsElement).open;
        const state = this.sectionState[sectionId];
        if (isOpen) {
            state.mounted?.(true);
        }
        state.open(isOpen);
    }

    private fieldsetAttr() {
        return { disabled: !this.rightPanelToggle() };
    }

    private onRestoreFactorySettingsClick = () => {
        discardAllSettings()
        this.rightPanelToggle(true)
    }

    private onDeleteCacheClick() {
        global.cacheManager.removeCache()
    }

    private getDisplayName(): string {
        const user = this.login.user();
        if (!user) return '';
        const firstName = getSyncFieldValue('GivenName', user.syncfields) || '';
        const lastName = getSyncFieldValue('Surname', user.syncfields) || '';
        return `${firstName} ${lastName}`.trim();
    }

    private getPrivacyPolicyHash(): string {
        return getHash('privacypolicy') ?? '';
    }

    private getDocumentationUrl(): string {
        return getLanguageProperty(this.config.URL_DOCUMENTATION);
    }

    private onLogoutClick() {
        global.login.doLogout();
    }

    private onClosePanelClick() {
        this.rightPanelToggle(false);
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.toggleDemoMode.dispose();
    }
}
